import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { formatCurrency } from '../../shared/utils/format';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Select,
  Textarea,
} from '@nexora/design-system';

/* ── Contratos reais ──
 * POST /logistics/ctes → CteDto (body: { number?, serie?, issuerId!, recipientId!,
 *   value?, freightValue?, route?, cargo?, notes?, emitir? })
 * GET /logistics/ctes/dashboard → KPIs
 */

interface PartyOption {
  id: string;
  name: string;
  document?: string | null;
  customerType?: string | null;
}

export function NewCtePage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const partiesQ = useQuery({
    queryKey: ['logistics', 'parties'],
    queryFn: async () => {
      const data = await apiClient.get<any>('/parties', { params: { page: '1', limit: '100' } });
      return data as { data: PartyOption[] };
    },
    enabled: !!tenantId,
  });
  const parties: PartyOption[] = partiesQ.data?.data ?? [];

  const [form, setForm] = useState({
    issuerId: '',
    recipientId: '',
    serie: '',
    route: '',
    value: '',
    freightValue: '',
    adValorem: '',
    gris: '',
    pedagio: '',
    notes: '',
    emitir: true,
  });

  const createCte = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        issuerId: form.issuerId,
        recipientId: form.recipientId,
        serie: form.serie || undefined,
        route: form.route || undefined,
        value: form.value ? Number(form.value) : undefined,
        freightValue: form.freightValue ? Number(form.freightValue) : undefined,
        cargo: [
          {
            adValorem: form.adValorem ? Number(form.adValorem) : 0,
            gris: form.gris ? Number(form.gris) : 0,
            pedagio: form.pedagio ? Number(form.pedagio) : 0,
          },
        ],
        notes: form.notes || undefined,
        emitir: form.emitir,
      };
      const res = await apiClient.post('/logistics/ctes', payload);
      return res;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['logistics', 'ctes'] });
      navigate({ to: '/logistics/ctes' as never });
    },
  });

  const set = (k: string, v: string | boolean): void => setForm((f) => ({ ...f, [k]: v }));

  const totalFreight = (Number(form.freightValue) || 0) + (Number(form.adValorem) || 0) + (Number(form.gris) || 0) + (Number(form.pedagio) || 0);

  return (
    <Card variant="outlined" padding="0" className="page-card">
      <CardHeader className="px-md pt-md">Novo Conhecimento de Transporte (CT-e)</CardHeader>
      <CardBody>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!form.issuerId || !form.recipientId) return;
            createCte.mutate();
          }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}
        >
          <Select label="Emitente *" value={form.issuerId} onChange={(e) => set('issuerId', e.target.value)}>
            <option value="">Selecione…</option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
          <Select label="Destinatário *" value={form.recipientId} onChange={(e) => set('recipientId', e.target.value)}>
            <option value="">Selecione…</option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
          <Input label="Série" value={form.serie} onChange={(e) => set('serie', e.target.value)} />
          <Input label="Rota (Orig → Dest)" value={form.route} onChange={(e) => set('route', e.target.value)} />

          <fieldset style={{ gridColumn: '1 / -1', border: '1px solid var(--outline-variant)', borderRadius: 8, padding: '1rem' }}>
            <legend style={{ fontWeight: 600 }}>Composição do Frete</legend>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
              <Input label="Valor do CT-e (R$)" type="number" value={form.value} onChange={(e) => set('value', e.target.value)} />
              <Input label="Frete Base (R$)" type="number" value={form.freightValue} onChange={(e) => set('freightValue', e.target.value)} />
              <Input label="Ad Valorem (%)" type="number" value={form.adValorem} onChange={(e) => set('adValorem', e.target.value)} />
              <Input label="GRIS (%)" type="number" value={form.gris} onChange={(e) => set('gris', e.target.value)} />
              <Input label="Pedágio (R$)" type="number" value={form.pedagio} onChange={(e) => set('pedagio', e.target.value)} />
            </div>
            <div style={{ marginTop: '0.75rem', fontWeight: 600 }}>
              Total Frete: {formatCurrency(totalFreight)}
            </div>
          </fieldset>

          <Textarea label="Observações" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.emitir} onChange={(e) => set('emitir', e.target.checked)} />
            Emitir imediatamente
          </label>

          {createCte.isError && (
            <div role="alert" style={{ gridColumn: '1 / -1', color: 'var(--error)' }}>
              Erro ao criar CT-e. Verifique os campos obrigatórios.
            </div>
          )}

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => navigate({ to: '/logistics/ctes' as never })}>Cancelar</Button>
            <Button type="submit" disabled={!form.issuerId || !form.recipientId || createCte.isPending}>
              {createCte.isPending ? 'Criando…' : 'Criar CT-e'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}